import { useState, useEffect, useRef } from 'react';
import { CodeInput } from './CodeInput';
import { sendTwoFactorCode, verifyTwoFactorCode } from '@/lib/api';

export interface TwoFactorVerificationProps {
  onVerify: (email: string, code: string) => Promise<boolean>;
  showEscrowCheckbox?: boolean;
  onComplete: () => void;
  /** Hide the Continue button (useful when used in wizards with external navigation) */
  hideContinueButton?: boolean;
  /** Optional callback to report state changes (verified, escrowChecked) */
  onStateChange?: (state: { verified: boolean; escrowChecked: boolean }) => void;
}

/**
 * TwoFactorVerification - 2FA verification component with email input and code entry
 * 
 * Features:
 * - Email input with "Send Code" button
 * - Loading states during API calls
 * - Success/error message display
 * - 6-digit code input (shown after code sent)
 * - Optional escrow checkbox (for buy flow)
 * - Continue button (disabled until code verified and checkbox checked if required)
 */
export const TwoFactorVerification = ({
  onVerify,
  showEscrowCheckbox = false,
  onComplete,
  hideContinueButton = false,
  onStateChange,
}: TwoFactorVerificationProps) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [escrowChecked, setEscrowChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const isVerifyingRef = useRef(false);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
        setIsNetworkError(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [successMessage]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendCode = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      setIsNetworkError(false);
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      setIsNetworkError(false);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setIsNetworkError(false);

    try {
      const result = await sendTwoFactorCode(email);
      if (result.success) {
        setCodeSent(true);
        setSuccessMessage('Code sent to your email');
        setCode(''); // Reset code input
        setIsVerified(false); // Reset verification status
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send verification code. Please try again.';
      const isNetwork = errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch');
      setError(errorMessage);
      setIsNetworkError(isNetwork);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6 || isVerifyingRef.current) {
      return;
    }

    isVerifyingRef.current = true;
    setLoading(true);
    setError(null);
    setIsNetworkError(false);

    try {
      const result = await verifyTwoFactorCode(email, code);
      if (result.verified) {
        setIsVerified(true);
        // Report state change if callback provided
        if (onStateChange) {
          onStateChange({ verified: true, escrowChecked });
        }
        // Call custom onVerify callback if provided
        if (onVerify) {
          await onVerify(email, code);
        }
      } else {
        setError('Invalid verification code. Please try again.');
        setCode(''); // Clear code for retry
        setIsNetworkError(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify code';
      const isNetwork = errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch');
      setError(errorMessage);
      setCode(''); // Clear code for retry
      setIsVerified(false);
      setIsNetworkError(isNetwork);
    } finally {
      setLoading(false);
      isVerifyingRef.current = false;
    }
  };

  // Auto-verify when code is complete (6 digits)
  useEffect(() => {
    if (code.length === 6 && codeSent && !isVerified && !loading && !isVerifyingRef.current) {
      handleVerify();
    }
    // Only depend on code, codeSent, isVerified, and loading to prevent duplicate calls
    // handleVerify is stable and doesn't need to be in deps due to ref guard
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, codeSent, isVerified, loading]);

  const handleContinue = () => {
    if (isVerified && (!showEscrowCheckbox || escrowChecked)) {
      onComplete();
    }
  };

  const canContinue = isVerified && (!showEscrowCheckbox || escrowChecked);

  return (
    <div className="w-full max-w-md mx-auto space-y-6">
      {/* Email Input Section */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
          Email Address
        </label>
        <div className="flex gap-2">
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading && !codeSent) {
                handleSendCode();
              }
            }}
            placeholder="Enter your email"
            disabled={loading || codeSent}
            className={`
              flex-1 h-11 px-4 py-2 border-2 rounded-md
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
              transition-colors
              ${error && !codeSent
                ? 'border-error bg-red-50'
                : 'border-border bg-background'
              }
              ${loading || codeSent
                ? 'bg-background-secondary cursor-not-allowed opacity-50'
                : 'hover:border-primary'
              }
            `}
            aria-label="Email address for verification code"
            aria-invalid={error && !codeSent ? true : false}
            aria-describedby={error && !codeSent ? 'email-error' : undefined}
          />
          <button
            type="button"
            onClick={handleSendCode}
            disabled={loading || codeSent || !email.trim()}
            className={`
              px-6 py-2 h-11 min-h-[44px] bg-primary text-white rounded-md
              font-medium hover:bg-primary-dark
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
            `}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Sending...</span>
              </div>
            ) : (
              'Send Code'
            )}
          </button>
        </div>
        {error && !codeSent && (
          <div className="mt-2">
            <p id="email-error" className="text-sm text-error" role="alert">
              {error}
            </p>
            {isNetworkError && (
              <button
                type="button"
                onClick={handleSendCode}
                disabled={loading}
                className="mt-2 px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Retry sending verification code"
              >
                Retry
              </button>
            )}
          </div>
        )}
        {successMessage && (
          <p className="mt-2 text-sm text-success" role="status">
            {successMessage}
          </p>
        )}
      </div>

      {/* Code Input Section (shown after code sent) */}
      {codeSent && (
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Enter Verification Code
          </label>
          <CodeInput
            length={6}
            onChange={setCode}
            autoFocus={true}
            disabled={loading || isVerified}
            error={!!error && code.length === 6}
          />
          {error && code.length === 6 && (
            <div className="mt-2">
              <p className="text-sm text-error" role="alert">
                {error}
              </p>
              {isNetworkError && (
                <button
                  type="button"
                  onClick={handleVerify}
                  disabled={loading || code.length !== 6}
                  className="mt-2 px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Retry verification"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {isVerified && (
            <p className="mt-2 text-sm text-success" role="status">
              Code verified successfully
            </p>
          )}
        </div>
      )}

      {/* Escrow Checkbox (shown if showEscrowCheckbox is true) */}
      {showEscrowCheckbox && codeSent && (
        <div className="flex items-start gap-2">
          <input
            id="escrow-checkbox"
            type="checkbox"
            checked={escrowChecked}
            onChange={(e) => {
              const checked = e.target.checked;
              setEscrowChecked(checked);
              // Report state change if callback provided
              if (onStateChange) {
                onStateChange({ verified: isVerified, escrowChecked: checked });
              }
            }}
            disabled={loading || !isVerified}
            className={`
              mt-1 w-5 h-5 rounded border-2 border-border
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
            `}
            aria-label="I understand that funds will be placed in escrow until transaction completion"
          />
          <label
            htmlFor="escrow-checkbox"
            className={`
              text-sm text-text-primary
              ${loading || !isVerified
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer'
              }
            `}
          >
            I understand that funds will be placed in escrow until transaction completion
          </label>
        </div>
      )}

      {/* Continue Button */}
      {codeSent && !hideContinueButton && (
        <button
          type="button"
          onClick={handleContinue}
          disabled={!canContinue || loading}
          className={`
            w-full h-11 min-h-[44px] bg-primary text-white rounded-md
            font-medium hover:bg-primary-dark
            focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          `}
        >
          Continue
        </button>
      )}
    </div>
  );
};

